import React, { useState, useEffect, useMemo, useRef } from 'react';

/**
 * Generic checkbox dropdown for picking several values at once.
 *
 * A plain <select multiple> was rejected for the same reasons as in
 * EntityMultiSelect: it needs ctrl/cmd-click to add a second item, silently wipes
 * the whole selection on a stray plain click, and cannot be searched. Picking six
 * cities out of a hundred and thirty is exactly what that control is worst at.
 *
 * Deliberately separate from EntityMultiSelect rather than a refactor of it. That
 * component's internals, aria-labels and empty-state copy are all bound to entity
 * names and types; reusing it for geography meant either an abusive shape adapter
 * or generalizing a component Field Intelligence depends on. Folding the two
 * together is a later, optional cleanup.
 *
 * Selection is committed to the caller on every tick via onChange. Callers that
 * trigger expensive work should hold a draft and act on onClose instead — see
 * FilterBar, where one commit per checkbox would refetch every page.
 *
 * Props:
 *   options     — [{ value, label, sublabel }]; value must be unique and stable
 *   selected    — array of `value`s
 *   onChange    — (values[]) => void, fired on every tick
 *   onClose     — () => void, fired when the menu closes (optional)
 *   placeholder — trigger text when nothing is selected
 *   name        — noun used in aria-labels and empty states, e.g. 'cities'
 *   searchable  — show the search box (default true)
 */
const s = {
  wrap: { position: 'relative', width: '100%' },
  trigger: {
    padding: '10px 14px', border: '1.5px solid var(--card-border)', borderRadius: '12px',
    background: 'var(--card-bg)', color: 'var(--text-dark)', fontSize: '14px',
    fontFamily: 'inherit', cursor: 'pointer', width: '100%', textAlign: 'left',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
    boxSizing: 'border-box',
  },
  menu: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 30,
    minWidth: '100%', width: 'max-content', maxWidth: '340px',
    background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden',
  },
  search: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '13px',
    fontFamily: 'inherit', border: 'none', borderBottom: '1px solid var(--card-border)',
    background: 'var(--card-bg)', color: 'var(--text-dark)', outline: 'none',
  },
  list: { maxHeight: '260px', overflowY: 'auto' },
  row: {
    display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 12px',
    fontSize: '13px', cursor: 'pointer', color: 'var(--text-dark)',
  },
  sublabel: { fontSize: '11px', color: 'var(--text-lighter)', marginLeft: 'auto', paddingLeft: '10px' },
  hint: { padding: '10px 12px', fontSize: '12px', color: 'var(--text-lighter)' },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderTop: '1px solid var(--card-border)', fontSize: '12px',
    color: 'var(--text-lighter)',
  },
  link: {
    background: 'none', border: 'none', padding: 0, fontFamily: 'inherit',
    fontSize: '12px', fontWeight: 600, color: 'var(--text-light)', cursor: 'pointer',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' },
  chip: {
    padding: '3px 10px', fontSize: '11px', fontWeight: 600, background: 'var(--bg-dark)',
    border: '1px solid var(--card-border)', borderRadius: '16px', cursor: 'pointer',
    color: 'var(--text-light)', fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', gap: '5px',
  },
};

export default function MultiSelect({
  options = [],
  selected = [],
  onChange,
  onClose,
  placeholder = '— select —',
  name = 'options',
  searchable = true,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  // onClose fires expensive work in the caller, so it must not run on the initial
  // render or on a close that follows no open.
  const wasOpen = useRef(false);

  useEffect(() => {
    const onDocDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, []);

  useEffect(() => {
    if (open) {
      wasOpen.current = true;
      return;
    }
    // Reset the search on close, so reopening never starts on a stale term that
    // hides everything the user is looking for.
    setQ('');
    if (wasOpen.current) {
      wasOpen.current = false;
      if (onClose) onClose();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const term = q.trim().toLowerCase();
  const visible = useMemo(() => (
    term ? options.filter(o => String(o.label).toLowerCase().includes(term)) : options
  ), [options, term]);

  const byValue = useMemo(() => {
    const m = new Map();
    options.forEach(o => m.set(o.value, o));
    return m;
  }, [options]);

  const toggle = (value) => {
    if (selected.includes(value)) onChange(selected.filter(v => v !== value));
    else onChange([...selected, value]);
  };

  const labelFor = v => (byValue.get(v)?.label ?? v);
  const triggerText = selected.length === 0
    ? placeholder
    : selected.length === 1
      ? labelFor(selected[0])
      : `${labelFor(selected[0])} + ${selected.length - 1} more`;

  return (
    <div ref={wrapRef} style={s.wrap}>
      <button
        type="button"
        style={s.trigger}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        /* Name carries purpose AND state: a bare "Select cities" would hide the
           current selection from a screen reader, which is the only thing the
           sighted user reads off this button. */
        aria-label={selected.length ? `Select ${name}, ${triggerText}` : `Select ${name}`}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {triggerText}
        </span>
        <span style={{ fontSize: '10px', color: 'var(--text-lighter)' }}>▼</span>
      </button>

      {/* The menu carries no role: its children are checkboxes, and a listbox role
          would promise options that are not there. Matches EntityMultiSelect. */}
      {open && (
        <div style={s.menu}>
          {searchable && (
            <input
              autoFocus
              style={s.search}
              placeholder={`Search ${name}…`}
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          )}
          <div style={s.list}>
            {options.length === 0 ? (
              <div style={s.hint}>No {name} available.</div>
            ) : visible.length === 0 ? (
              <div style={s.hint}>No matches for “{q.trim()}”</div>
            ) : (
              visible.map(o => {
                const checked = selected.includes(o.value);
                return (
                  <label
                    key={o.value}
                    style={s.row}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-dark)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(o.value)}
                      style={{ cursor: 'pointer' }}
                    />
                    <span>{o.label}</span>
                    {o.sublabel && <span style={s.sublabel}>{o.sublabel}</span>}
                  </label>
                );
              })
            )}
          </div>
          <div style={s.footer}>
            <span>{selected.length} selected</span>
            {selected.length > 0 && (
              <button type="button" style={s.link} onClick={() => onChange([])}>Clear all</button>
            )}
          </div>
        </div>
      )}

      {selected.length > 0 && (
        <div style={s.chips}>
          {selected.map(v => (
            <button
              key={v}
              type="button"
              onClick={() => { toggle(v); if (!open && onClose) onClose(); }}
              title={`Remove ${labelFor(v)}`}
              style={s.chip}
            >
              {labelFor(v)} <span style={{ fontSize: '13px', color: 'var(--text-lighter)' }}>×</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
